// Where a link that leaves the app goes from a Home Screen web app on an iPhone
// or iPad, which has no tabs, Done or back button: a page opened there takes
// over the app's full-screen window. The Intune web clip "Higgins Apps" also
// ignores the manifest scope, so Apple shows no Safari bar either (#615). In
// such an app the link opens in another browser instead, by its URL scheme:
// - `safari`: `x-safari-https://…`. Not documented by Apple; reported working
//   on recent iPadOS (checked 7 October 2026).
// - `edge`: `microsoft-edge-https://…`, Microsoft's documented deep link for
//   Edge for iOS ("Manage Microsoft Edge on iOS and Android with Intune",
//   Deeplink methods). Edge is on every managed Higgins device (IT, 27 Oct
//   2026); Defender for Cloud Apps asks for it on SharePoint anyway.
// No imports, so node tests of the apps can load this file on its own
// (`@higgins/utils/outbound-href`).

export type OutboundBrowser = 'safari' | 'edge';

const SCHEME_PREFIX: Record<OutboundBrowser, string> = {
  safari: 'x-safari-',
  edge: 'microsoft-edge-',
};

/** True in a Home Screen web app on an iPhone or iPad. `navigator.standalone` exists only in Apple's WebKit. */
export function isIosHomeScreenApp(nav: Navigator | undefined = typeof navigator === 'undefined' ? undefined : navigator): boolean {
  return (nav as (Navigator & { standalone?: boolean }) | undefined)?.standalone === true;
}

/**
 * The address a link out of the app uses: in a Home Screen app, the chosen
 * browser's scheme for an https address; otherwise, or for anything else, the
 * address as it is.
 */
export function outboundHref(url: string, browser: OutboundBrowser, homeScreen: boolean = isIosHomeScreenApp()): string {
  return homeScreen && url.startsWith('https://') ? `${SCHEME_PREFIX[browser]}${url}` : url;
}
